import cn from "classnames";
import { Datetime, Field } from "components/shared/form";
import {
    bidPaymentStatuses,
    bidPaymentStatusesEnum,
    distributionBidPaymentTypesEnum,
    featureModuleTypesEnum,
} from "helpers/enums";
import { enumLabel, formatNumber } from "helpers/system";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Popover } from "react-tiny-popover";
import { applyPayment } from "sources/books/distributionBids";
import { payableStatuses, paymentFieldsConfig } from "./paymentFields";

const paymentStatusClassName = (payment) => {
    if (payment.paymentStatus === bidPaymentStatusesEnum.Paid) {
        return "badge-success";
    }
    const planDate = moment(payment.planPaymentDate, "YYYY-MM-DD", true);
    const overdueDays = planDate.isValid() ? moment().startOf("day").diff(planDate, "days") : 0;
    if (overdueDays > 1) {
        return "badge-danger";
    }
    const daysUntilPayment = planDate.isValid() ? planDate.diff(moment().startOf("day"), "days") : null;
    if (daysUntilPayment !== null && daysUntilPayment >= 0 && daysUntilPayment <= 1) {
        return "badge-warning";
    }
    return "badge-secondary";
};

const DistributionBidPaymentStatus = ({
    bid,
    bidPolicy,
    dataTestId,
    isAccountsPayable = false,
    organization,
    paymentType,
    profile,
    refresh,
    roles,
}) => {
    const paymentProperty =
        paymentType === distributionBidPaymentTypesEnum.HiredCar ? "hiredCarPayment" : "customerPayment";
    const initialPayment = useMemo(
        () => ({
            ...(bid[paymentProperty] || {}),
            paymentStatus: bid[paymentProperty]?.paymentStatus || bidPaymentStatusesEnum.NotPaid,
        }),
        [bid, paymentProperty],
    );
    const [opened, setOpened] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [payment, setPayment] = useState(initialPayment);
    const hasAccountsReceivable = profile.availableFeatureModules.includes(featureModuleTypesEnum.AccountsReceivable);
    const canEdit = hasAccountsReceivable && bidPolicy.canEditPaymentStatusInShow();
    const config = paymentFieldsConfig(isAccountsPayable);
    const statuses = isAccountsPayable ? payableStatuses : bidPaymentStatuses;

    useEffect(() => setPayment(initialPayment), [initialPayment]);

    const handleChange = useCallback(({ name, value }) => {
        if (["invoiceDate", "planPaymentDate", "factPaymentDate"].includes(name) && value) {
            value = moment(value).format("YYYY-MM-DD");
        }
        const nextValue = { [name]: value };
        if (name === "paymentStatus") {
            if (value !== bidPaymentStatusesEnum.PartiallyPaid) {
                nextValue.remainingPayment = null;
            }
            if (value !== bidPaymentStatusesEnum.Paid) {
                nextValue.factPaymentDate = null;
            }
        }
        setPayment((current) => ({ ...current, ...nextValue }));
        setError("");
    }, []);

    const closePopover = useCallback(() => {
        setOpened(false);
        setPayment(initialPayment);
        setError("");
    }, [initialPayment]);

    const savePayment = useCallback(() => {
        if (payment.paymentStatus !== bidPaymentStatusesEnum.NotPaid && !payment.planPaymentDate) {
            setError(`Укажите ${config.planPaymentDate.label.toLowerCase()}`);
            return;
        }
        if (payment.paymentStatus === bidPaymentStatusesEnum.Paid && !payment.factPaymentDate) {
            setError(`Укажите ${config.factPaymentDate.label.toLowerCase()}`);
            return;
        }
        if (
            payment.paymentStatus === bidPaymentStatusesEnum.PartiallyPaid &&
            (payment.remainingPayment === null ||
                payment.remainingPayment === undefined ||
                payment.remainingPayment === "")
        ) {
            setError(`Укажите ${config.remainingPayment.label.toLowerCase()}`);
            return;
        }
        if (
            initialPayment.planPaymentDate &&
            payment.planPaymentDate !== initialPayment.planPaymentDate &&
            !payment.comment
        ) {
            setError("Укажите комментарий при изменении плановой даты");
            return;
        }
        const { invoiceDate, planPaymentDate, factPaymentDate, paymentStatus, remainingPayment, comment } = payment;
        setLoading(true);
        applyPayment({
            distributionBidId: bid.id,
            paymentType,
            payment: { invoiceDate, planPaymentDate, factPaymentDate, paymentStatus, remainingPayment, comment },
        }).then(
            (response) => {
                if (response) {
                    setOpened(false);
                    refresh();
                }
                setLoading(false);
            },
            () => setLoading(false),
        );
    }, [bid.id, config, initialPayment.planPaymentDate, payment, paymentType, refresh]);

    if (roles.isTruckingOrganizationDispatcher || (!hasAccountsReceivable && !bid[paymentProperty])) {
        return null;
    }

    const statusLabel = enumLabel(statuses, payment.paymentStatus);
    const status = (
        <>
            <span className={cn("badge", paymentStatusClassName(payment))}>{statusLabel}</span>
            {payment.paymentStatus === bidPaymentStatusesEnum.PartiallyPaid && (
                <span className="small ml-2">
                    {config.remainingPayment.label}:{" "}
                    {formatNumber(payment.remainingPayment || 0, {
                        style: "currency",
                        currency: organization?.currency,
                    })}
                </span>
            )}
        </>
    );

    if (!canEdit) {
        return <span data-test-id={dataTestId}>{status}</span>;
    }

    const dateField = (field) => (
        <Datetime
            key={field}
            labelWrapper="col-12 small mb-0"
            wrapper="col-12"
            inPortal
            {...config[field]}
            libOptions={{ className: "form-control form-control-xs" }}
            onChange={handleChange}
            value={payment[field]}
        />
    );
    const popoverContent = (
        <div className={cn("popover w-auto font-size-14", { "loading loading--small": loading })}>
            <div className="font-size-16 pb-2">Изменение данных об оплате</div>
            <div className="form-group">
                <label className="small mb-0" htmlFor={`${dataTestId}-select`}>
                    {config.paymentStatus.label}
                </label>
                <select
                    id={`${dataTestId}-select`}
                    className="form-control form-control-xs"
                    onChange={(event) => handleChange({ name: "paymentStatus", value: event.target.value })}
                    value={payment.paymentStatus}
                >
                    {statuses.map((statusOption) => (
                        <option key={statusOption.value} value={statusOption.value}>
                            {statusOption.label}
                        </option>
                    ))}
                </select>
            </div>
            {dateField("invoiceDate")}
            {dateField("planPaymentDate")}
            {payment.paymentStatus === bidPaymentStatusesEnum.Paid && dateField("factPaymentDate")}
            {payment.paymentStatus === bidPaymentStatusesEnum.PartiallyPaid && (
                <Field
                    labelWrapper="col-12 small mb-0"
                    fieldWrapper="col-12"
                    {...config.remainingPayment}
                    onChange={handleChange}
                    value={payment.remainingPayment ?? ""}
                />
            )}
            <Field
                labelWrapper="col-12 small mb-0"
                fieldWrapper="col-12"
                {...config.comment}
                onChange={handleChange}
                value={payment.comment ?? ""}
            />
            {error && <div className="text-danger small mb-2">{error}</div>}
            <div className="d-flex justify-content-between mb-2">
                <button type="button" className="btn btn-xs btn-secondary" onClick={closePopover}>
                    Отмена
                </button>
                <button type="button" className="btn btn-xs btn-brand" onClick={savePayment}>
                    Сохранить
                </button>
            </div>
        </div>
    );

    return (
        <Popover
            isOpen={opened}
            content={popoverContent}
            containerStyle={{ zIndex: 130 }}
            onClickOutside={(event) => opened && !event.target.closest(".react-datepicker") && closePopover()}
        >
            <button
                type="button"
                aria-expanded={opened}
                aria-label={`Изменить ${config.paymentStatus.label.toLowerCase()}: ${statusLabel}`}
                className="show-bid__payment-status show-bid__payment-status--editable"
                data-test-id={dataTestId}
                disabled={loading}
                onClick={() => {
                    setOpened((value) => !value);
                    setError("");
                }}
            >
                {status}
                <i aria-hidden="true" className="icon-uEA10-pen show-bid__payment-status-icon" />
            </button>
        </Popover>
    );
};

export default DistributionBidPaymentStatus;
